import { CommonModule, DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, Output, OnChanges } from '@angular/core';
import { Firestore, collection, doc, setDoc, docData, addDoc } from '@angular/fire/firestore';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { IonButton, IonList, IonItem, IonTextarea } from '@ionic/angular/standalone';
import { RouterModule } from '@angular/router';
import { BidService } from '../../core/bid.service';
import { Application } from '../../models/application.interface';
import { Bid } from '../../models/bid.interface';
import { Msg } from "../../models/msg.interface";
import { DatabaseProvider } from '../../providers/database';
import { Observable } from 'rxjs';

interface ApplicationStatus {
  applied: string | null;
  rejected: string | null;
  accepted: string | null;
}

@Component({
    imports: [
        CommonModule, 
        DatePipe, 
        ReactiveFormsModule,
        RouterModule,
        IonButton,
        IonList,
        IonItem,
        IonTextarea
    ],
    selector: 'apply-gig',
    templateUrl: 'apply-gig.html',
    standalone: true
})
export class ApplyGigComponent implements OnChanges {
  hasApplied: Observable<ApplicationStatus>;
  isApply: boolean = false;
  form: FormGroup;
  time: number = 0;
  pairid: string = '';
  applyPayload!: Application;
  bidPayload!: Bid;
  msgPayload!: Msg;
  customer: any;
  gig: any;
  provider: any;

  @Input() customerid: any;
  @Input() gigid: string = '';
  @Input() uid: string = '';
  @Input() title: any;

  @Output() saved = new EventEmitter<boolean>();

  constructor(
    private bs: BidService,
    public db: DatabaseProvider, 
    private firestore: Firestore,
    private fb: FormBuilder, 
  ) {
    this.hasApplied = new Observable<ApplicationStatus>();
    this.form = this.fb.group({
      message: ['', Validators.compose([Validators.required, Validators.minLength(2), Validators.maxLength(255)])],
    });
  }

  save() {
    const date = Math.floor(Date.now());
    this.applyPayload = {
      agree: null,
      applied: date,
      alias: this.provider.alias, 
      customer: this.customerid,
      gig: this.gigid, 
      initials: this.provider.initials,
      message: this.form.value.message,
      pic: this.provider.pic,
      rating: this.provider.rating,
      reject: null,
      uid: this.provider.uid, 
    }

    const userBidRef = doc(this.firestore, `ge_users/${this.uid}/bids/${this.gigid}`);
    const gigsterRef = doc(this.firestore, `ge_gigs/${this.gigid}/gigsters/${this.uid}`);

    setDoc(userBidRef, this.applyPayload, { merge: true }).then(() => {
      setDoc(gigsterRef, this.applyPayload, { merge: true }).then(() => {
        this.saved.emit(true);
        this.time = new Date().getTime();
        this.pairid = this.customerid + this.gigid + this.uid;

        this.msgPayload = {
          message: this.form.value.message,
          pair: this.pairid,
          recipient: this.customerid,
          sender: this.uid,
          time: this.time,
          title: this.title
        }

        this.bidPayload = {
          customer: {
            agree: false, 
            alias: this.customer.alias, 
            id: this.customer.uid, 
            initials: this.customer.initials,
            pic: this.customer.pic,
            rating: this.customer.rating,
            unread: 0
          },
          provider: {
            agree: false, 
            alias: this.provider.alias, 
            id: this.provider.uid, 
            initials: this.provider.initials,
            pic: this.provider.pic,
            rating: this.provider.rating,
            unread: 0
          },
          ad: {
            id: this.gigid, 
            overview: this.gig.overview, 
            stage: 'negotiate', 
            title: this.gig.title,
            type: 'gig'
          },
          messages: 0,
          pair: this.pairid,
          time: this.time,
          total: this.gig.total,
        }

        this.bs
          .createBid(this.pairid, this.bidPayload)
          .then(() => {
            this.bs.addBid(this.pairid, this.msgPayload)
            this.bs.updateCurrent(this.pairid, this.msgPayload)
            .then(() => {
              this.isApply = false;
              const email = this.customer.email;
              const body = this.form.value.message;
              const msgPayload = {
                from: 'support@gigexchange.com',
                to: email,
                message: {
                  subject: 'Application for ' + this.gig.title,
                  text: body,
                  html: body,
                },
                replyTo: 'support@gigexchange.com',
                sendGrid: {
                  templateId: 'd-ce67c8749da6482f827c84ee4bcd516e',
                  dynamicTemplateData: {
                    message: {
                      subject: 'Congratulations, you have had an Application to your Ad on gigexchange: ' + this.gig.title,
                      text: body,
                      html: body,
                    }
                  }
                }
              }
              const docRef = collection(this.firestore, 'ge_support');
              addDoc(docRef, { ...this.form.value, ...msgPayload })
              .catch((error: any) => {
                console.error('Error saving message:', error);
              })          
            })
            .catch(err => {
              console.error(err);
            })
          })
          .catch(err => {
            console.error(err);
          });
      });
    });
  }
  ngOnChanges() {
    this.hasApplied = this.db.gigSters(this.uid, this.gigid) as Observable<ApplicationStatus>;

    const providerRef = doc(this.firestore, `ge_users/${this.uid}`);
    docData(providerRef).subscribe((provider: any) => {
      this.provider = provider;
    });

    const gigRef = doc(this.firestore, `ge_gigs/${this.gigid}`);
    docData(gigRef).subscribe(gig => {
      this.gig = gig;
    });

    const customerRef = doc(this.firestore, `ge_users/${this.customerid}`);
    docData(customerRef).subscribe(customer => {
      this.customer = customer;
    });
  }

  // convenience getter for easy access to form fields
  get f() { return this.form.controls; }
}